import './Modal.css';
import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { isFunction } from 'lodash';
import { Card } from '../Card/Card';

type RenderFunction = () => React.ReactNode;

interface ModalContextType {
  footer: HTMLDivElement | null;
}

export const ModalContext = React.createContext<ModalContextType>({ footer: null });

export interface Props extends GlobalProps {
  open?: boolean;
  title?: React.ReactNode;
  size?: 'small' | 'large' | 'xs';
  footer?: React.ReactNode;
  children: React.ReactNode | RenderFunction;
  actions?: React.ReactNode;
  overflow?: boolean;
  saveTitleOverride?: string;
  position?: 'center' | 'aside';
  scrollShadows?: boolean;
}

export function Modal({
  children,
  title,
  size = 'large',
  footer,
  className,
  position = 'center',
  style,
  open,
  overflow,
  actions,
  scrollShadows = true,
}: Props): JSX.Element {
  const [localOpen, setLocalOpen] = useState(false);
  const [displayChildren, setDisplayChildren] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);

  if (open && !localOpen) {
    setDisplayChildren(true);
    setLocalOpen(true);
  }
  if (!open && localOpen) {
    setLocalOpen(false);
    setTimeout(() => {
      setDisplayChildren(false);
    }, 300);
  }

  return (
    <ModalContext.Provider value={{ footer: footerRef.current }}>
      <div
        className={classnames('modal', `modal--${size}`, `modal--${position}`, className, {
          'modal--open': localOpen,
          'modal--overflow': overflow,
        })}
        style={style}
      >
        <div className="modal__background" />
        <Card
          title={title}
          size="large"
          footer={footer ?? <footer className="modal__footer" ref={footerRef}></footer>}
          elevation={8}
          actions={actions}
          scrollShadows={!overflow && scrollShadows}
        >
          {displayChildren && (isFunction(children) ? children() : children)}
        </Card>
      </div>
    </ModalContext.Provider>
  );
}
